import { createContext, useContext } from 'react';

import type { RiderProfile } from '@/api/types';
import { useLocationPing, type LocationPing } from '@/hooks/use-location-ping';

type RiderSession = {
  rider: RiderProfile;
  ping: LocationPing;
};

const RiderSessionContext = createContext<RiderSession | null>(null);

/**
 * The location stream, started ONCE for the whole signed-in session.
 *
 * Two screens want it — the account screen reports whether dispatch can see
 * the rider, and the run sheet orders the drops by distance from where they
 * are — and calling the hook in both would run two intervals: two GPS
 * wake-ups and two POSTs every twenty seconds, all shift, for one position.
 *
 * Mounted at the layout rather than inside a tab so it does not stop the
 * moment the rider taps away from the screen that happened to start it.
 */
export function RiderSessionProvider({
  rider,
  children,
}: {
  rider: RiderProfile;
  children: React.ReactNode;
}) {
  const ping = useLocationPing(rider);
  return (
    <RiderSessionContext.Provider value={{ rider, ping }}>{children}</RiderSessionContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRiderSession(): RiderSession {
  const value = useContext(RiderSessionContext);
  if (!value) throw new Error('useRiderSession must be used inside <RiderSessionProvider>');
  return value;
}
